import { cn } from '@/lib/utils';
import { attributeLabelWidthStyle } from '@/components/attributes';
import { ATTRIBUTE_VALUE_OFFSET } from './attribute-row';
import { FormField, type FormFieldVariant } from './FormField';
import { ValueConfirmationSlot } from './value-confirmation-slot';

interface ValuePairFieldsProps {
  mode: 'read' | 'edit';
  leftLabel: string;
  leftValue: number | undefined;
  rightLabel: string;
  rightValue: number | undefined;
  onLeftChange?: (value: number) => void;
  onRightChange?: (value: number) => void;
  leftPlaceholder?: string;
  rightPlaceholder?: string;
  leftError?: string; // Validation error for left field
  rightError?: string; // Validation error for right field
  currency?: boolean; // Format as currency (default: true)
  clickToCopy?: boolean; // Enable click-to-copy in read mode (default: false)
  /** When 'enrichment', renders the left/right value in blue with a NEW tag (read mode only) */
  leftChangeType?: 'enrichment';
  rightChangeType?: 'enrichment';
  /**
   * Liability forms only: a negative left/right value is a valid "credit"
   * (the institution owes the estate money) rather than a mistake — shows a
   * small explanatory hint under the field instead of leaving the negative
   * figure unexplained (LF-5563).
   */
  negativeIsCredit?: boolean;
  /**
   * Which chrome the pair wears (LF-6314). Defaults to `form`, so all 22
   * inventory forms are unchanged.
   *
   * **It does not reach the confirmation control**, deliberately: that arrives
   * through `ValueConfirmationSlot` from whichever host provided it, so the host
   * sets both — one decision per surface instead of two that can disagree.
   */
  variant?: FormFieldVariant;
}

/** Small explanatory hint shown under a field holding a negative "credit" value. */
function CreditHint({ compact = false }: { compact?: boolean }) {
  return (
    <p
      className={cn(
        'text-muted-foreground text-xs',
        // Under the figure it explains, on the compact row's own column.
        compact && ATTRIBUTE_VALUE_OFFSET
      )}
    >
      Negative value, recorded as a credit (the institution owes the estate).
    </p>
  );
}

/**
 * Reusable component for paired value fields (e.g., DoD Value + Current Value)
 * Displays as side-by-side fields with consistent formatting
 */
export function ValuePairFields({
  mode,
  leftLabel,
  leftValue,
  rightLabel,
  rightValue,
  onLeftChange,
  onRightChange,
  leftPlaceholder = '0.00',
  rightPlaceholder = '0.00',
  leftError,
  rightError,
  currency = true,
  clickToCopy = false,
  leftChangeType,
  rightChangeType,
  negativeIsCredit = false,
  variant = 'form'
}: ValuePairFieldsProps) {
  const showLeftCreditHint =
    negativeIsCredit && typeof leftValue === 'number' && leftValue < 0;
  const showRightCreditHint =
    negativeIsCredit && typeof rightValue === 'number' && rightValue < 0;

  if (mode === 'read') {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-4">
          {/* Left (DoD) value with its own confirmation control beneath it. */}
          <div className="flex flex-col gap-2">
            <FormField
              label={leftLabel}
              value={leftValue}
              mode="read"
              currency={currency}
              clickToCopy={clickToCopy}
              changeType={leftChangeType}
            />
            {showLeftCreditHint && <CreditHint />}
            <ValueConfirmationSlot kind="dod" />
          </div>
          {/* Right (current) value with its confirmation control beneath it. */}
          <div className="flex flex-col gap-2">
            <FormField
              label={rightLabel}
              value={rightValue}
              mode="read"
              currency={currency}
              clickToCopy={clickToCopy}
              changeType={rightChangeType}
            />
            {showRightCreditHint && <CreditHint />}
            <ValueConfirmationSlot kind="current" />
          </div>
        </div>
      </div>
    );
  }

  /*
    **The compact pair: two columns of attribute rows on the grid's own label
    column** (LF-6314, LF-6311 gap **A** / **C**).

    The two figures stay side by side and each keeps its own confirmation
    underneath — that pairing is the single strongest argument against collapsing
    the pair into two plain rows, so it survives the restyle unchanged. What
    changes is the chrome: a stacked `text-sm` label over a grey-filled bordered
    box becomes a label column and a value cell, on the row rhythm the rest of the
    surface keeps.

    Three details are load-bearing:

    · **`@container` and the label-width variable are on the same element**, which
      is what `attributeLabelWidthStyle` exists to guarantee — split across two and
      the `cqw` resolves against the wrong box.
    · **`gap-x-8`, not the `gap-3` the form variant uses.** The two-column arm of
      `ATTRIBUTE_LABEL_WIDTH` is derived from `row = (100cqw - 8 * --spacing) / 2`;
      a different gap here would silently make every label in this block a
      different width from the rows above it.
    · **Read mode is shared with the form variant** for the same reason
      `FormFieldCompact` has no read-out: a read-only attribute record has no
      defined appearance (LF-6311 gap **K**, unsolved).
  */
  if (variant === 'compact') {
    return (
      <div
        className="@container grid grid-cols-2 items-start gap-x-8"
        style={attributeLabelWidthStyle(2)}
      >
        <div className="flex min-w-0 flex-col">
          <FormField
            variant="compact"
            label={leftLabel}
            value={leftValue}
            mode="edit"
            type="number"
            step="0.01"
            placeholder={leftPlaceholder}
            onChange={(value) =>
              onLeftChange?.(
                typeof value === 'number'
                  ? value
                  : parseFloat(String(value)) || 0
              )
            }
            error={leftError}
            currency={currency}
          />
          {showLeftCreditHint && <CreditHint compact />}
          <ValueConfirmationSlot kind="dod" />
        </div>
        <div className="flex min-w-0 flex-col">
          <FormField
            variant="compact"
            label={rightLabel}
            value={rightValue}
            mode="edit"
            type="number"
            step="0.01"
            placeholder={rightPlaceholder}
            onChange={(value) =>
              onRightChange?.(
                typeof value === 'number'
                  ? value
                  : parseFloat(String(value)) || 0
              )
            }
            error={rightError}
            currency={currency}
          />
          {showRightCreditHint && <CreditHint compact />}
          <ValueConfirmationSlot kind="current" />
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 items-start gap-3">
      {/* Left column: the DoD-value field with its confirmation control nested
          directly beneath it, mirroring the right column. */}
      <div className="flex flex-col gap-2">
        <FormField
          label={leftLabel}
          value={leftValue}
          mode="edit"
          type="number"
          step="0.01"
          placeholder={leftPlaceholder}
          onChange={(value) =>
            onLeftChange?.(
              typeof value === 'number' ? value : parseFloat(String(value)) || 0
            )
          }
          error={leftError}
          currency={currency}
        />
        {showLeftCreditHint && <CreditHint />}
        <ValueConfirmationSlot kind="dod" />
      </div>
      {/* Right column: the current-value field with its confirmation control
          nested directly beneath it, so the "Confirmed against a source"
          checkbox reads as belonging to the current value — not the DoD value. */}
      <div className="flex flex-col gap-2">
        <FormField
          label={rightLabel}
          value={rightValue}
          mode="edit"
          type="number"
          step="0.01"
          placeholder={rightPlaceholder}
          onChange={(value) =>
            onRightChange?.(
              typeof value === 'number' ? value : parseFloat(String(value)) || 0
            )
          }
          error={rightError}
          currency={currency}
        />
        {showRightCreditHint && <CreditHint />}
        <ValueConfirmationSlot kind="current" />
      </div>
    </div>
  );
}
